/**
 * imageCropRotate.ts — non-destructive CROP + ROTATE for floor-plan / location
 * images, with a BAKED derived image as the canonical tracing + report surface.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY BAKE (the load-bearing decision — read before changing anything):
 *
 *  The boundary tracer captures the polygon in the image's NATURAL PIXEL space
 *  (SVG viewBox `0 0 W H`), and the report (`fetchFloorPlans.js` → renderGridSvg
 *  draws `<image x=0 y=0 width=W height=H>` UNDER the grid) shares that EXACT
 *  frame — "zero coordinate transform" (memory OQ-C). If we rotated/cropped the
 *  image only at DISPLAY time, every traced coordinate AND the report image would
 *  need the same transform applied — two code paths, easy to drift.
 *
 *  Instead we BAKE: produce a NEW derived raster (cropped + rotated pixels burnt
 *  in) and make THAT the canonical image for BOTH on-screen tracing AND the
 *  report. The derived image has its own natural W×H; tracing happens on the
 *  baked frame → zero drift, exactly like an un-edited upload. The report stage
 *  reads `floorplan.{storagePath,width,height}` and consumes the derived image
 *  with ZERO functions change.
 *
 *  NON-DESTRUCTIVE ("client verbatim is sacrosanct"): the ORIGINAL upload is
 *  never mutated. The derived image is a new Storage object (a `derived-…`
 *  filename in the same `floorplans/{floorId}/` path) and the FloorPlan record
 *  remembers the original via `original` + the recipe via `edit` so an editor
 *  can be re-opened and the original recovered. Re-baking overwrites the prior
 *  derived object cleanly.
 *
 *  PURE/IMPURE split: the geometry (rotated-bbox size, crop-rect math, recipe
 *  normalisation, FloorPlan derivation) is PURE + unit-tested. The single
 *  canvas-touching `bakeImage` (and the File→bitmap loader) sit at the bottom.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  "ROTATE THEN CROP" — the operation order (owner-locked 2026-06-14):
 *
 *  The owner rotates ONLY to STRAIGHTEN a tilted scan, then draws a normal
 *  UPRIGHT (axis-aligned) crop box around the part he wants. The output must be
 *  that straightened, upright, cropped region. So the bake order is
 *  **ROTATE → CROP**, NOT crop→rotate:
 *
 *    1. ROTATE the source by `deg` onto a canvas sized to the rotated bounding
 *       box (W' × H', see `rotatedBBox`). This is the "straightened image" — the
 *       whole rotated picture, axis-aligned to the screen.
 *    2. CROP the user's UPRIGHT rectangle out of that straightened canvas. The
 *       crop rect therefore lives in the DISPLAYED (rotated-bbox) frame, not in
 *       source-pixel space.
 *
 *  This is why the crop overlay in the editor is AXIS-ALIGNED (never rotated):
 *  the user draws an upright box over the rotated image, and that box is the
 *  crop in the rotated-bbox frame. `bakedSize` of a cropped recipe is simply the
 *  crop rect's own w×h (the upright region the user selected).
 * ─────────────────────────────────────────────────────────────────────────
 */

/* ════════════════════ pure: the edit recipe ════════════════════ */

/**
 * A crop rectangle expressed in the DISPLAYED (rotated-bounding-box) frame —
 * i.e. axis-aligned to the screen over the rotated/straightened image. x,y =
 * top-left; w,h = size. The editor drags this UPRIGHT over the rotated image;
 * baking rotates the source first, THEN crops to this rect.
 *
 * (When `rotateDeg == 0` the rotated bbox equals the source, so the rect is
 * also in source-pixel space — a plain crop is unchanged.)
 */
export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The full, replayable edit recipe stored alongside the derived image. Order of
 * operations is fixed: ROTATE (degrees, clockwise) → CROP (an UPRIGHT rect in
 * the rotated-bounding-box frame). A recipe with `crop=null` + `rotateDeg=0` is
 * the identity (no derived image).
 */
export interface EditRecipe {
  /** UPRIGHT crop rect in the rotated-bbox (displayed) frame, or null = no crop. */
  crop: CropRect | null;
  /** clockwise rotation in degrees applied BEFORE the crop (straightening). */
  rotateDeg: number;
}

export const IDENTITY_RECIPE: EditRecipe = { crop: null, rotateDeg: 0 };

/** PURE — clamp + normalise a degree value to [0, 360). NaN → 0. */
export function normaliseDeg(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  let d = deg % 360;
  if (d < 0) d += 360;
  // collapse a hair below 360 back to 0 for clean 90/180/270 quick rotations
  if (Math.abs(d - 360) < 1e-9) d = 0;
  return d;
}

/** PURE — does this recipe actually change the image? (identity → no bake). */
export function recipeIsIdentity(r: EditRecipe | null | undefined): boolean {
  if (!r) return true;
  const rot = normaliseDeg(r.rotateDeg);
  return r.crop == null && rot === 0;
}

/**
 * PURE — the rotated bounding box of a W×H source rotated by `deg`. This is the
 * size of the "straightened image" the user crops against (the displayed frame).
 * At deg=0 it is the source itself. Whole pixels.
 *   W' = |W·cos| + |H·sin|   H' = |W·sin| + |H·cos|
 */
export function rotatedBBox(
  W: number,
  H: number,
  deg: number,
): { width: number; height: number } {
  if (!(W > 0) || !(H > 0)) return { width: Math.max(1, Math.round(W) || 1), height: Math.max(1, Math.round(H) || 1) };
  const d = normaliseDeg(deg);
  if (d === 0) return { width: Math.round(W), height: Math.round(H) };
  const rad = (d * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  return {
    width: Math.max(1, Math.round(W * cos + H * sin)),
    height: Math.max(1, Math.round(W * sin + H * cos)),
  };
}

/**
 * PURE — clamp a crop rect to lie fully inside a W×H box (the DISPLAYED /
 * rotated-bbox frame, NOT the source). A degenerate or out-of-bounds rect is
 * snapped back in; a zero-area result returns null ("no crop"). Coordinates are
 * floored/ceiled to whole pixels so the baked canvas has integer dimensions.
 */
export function clampCrop(
  crop: CropRect | null | undefined,
  W: number,
  H: number,
): CropRect | null {
  if (!crop) return null;
  if (!(W > 0) || !(H > 0)) return null;
  let x = Math.max(0, Math.floor(crop.x));
  let y = Math.max(0, Math.floor(crop.y));
  let w = Math.ceil(crop.w);
  let h = Math.ceil(crop.h);
  if (!(w > 0) || !(h > 0)) return null;
  if (x >= W) x = W - 1;
  if (y >= H) y = H - 1;
  if (x + w > W) w = W - x;
  if (y + h > H) h = H - y;
  if (w <= 0 || h <= 0) return null;
  // a crop that is the entire frame = no crop
  if (x === 0 && y === 0 && w === W && h === H) return null;
  return { x, y, w, h };
}

/**
 * PURE — re-map an UPRIGHT crop rect from one displayed frame to another when the
 * straightening angle changes (so the crop box PERSISTS through rotation instead
 * of being cleared — owner-locked 2026-06-15).
 *
 * The crop lives in the DISPLAYED (rotated-bbox) frame, whose size changes with
 * the angle (`oldW×oldH` → `newW×newH`). The owner straightens the image and
 * wants the crop box to STAY PUT and keep showing the region inside it — NOT be
 * wiped, and NOT tilt with the image (it stays axis-aligned/upright). We keep the
 * crop in roughly the same RELATIVE position+size by scaling it by the per-axis
 * frame ratio, then clamp it back inside the new frame so it is always sensible +
 * visible. A null/identity input (no crop) stays null.
 */
export function rescaleCropToFrame(
  crop: CropRect | null | undefined,
  oldW: number,
  oldH: number,
  newW: number,
  newH: number,
): CropRect | null {
  if (!crop) return null;
  if (!(oldW > 0) || !(oldH > 0) || !(newW > 0) || !(newH > 0)) return null;
  const sx = newW / oldW;
  const sy = newH / oldH;
  const scaled: CropRect = {
    x: crop.x * sx,
    y: crop.y * sy,
    w: crop.w * sx,
    h: crop.h * sy,
  };
  return clampCrop(scaled, newW, newH);
}

/* ════════════════════ pure: crop-drag resolution ════════════════════ */

/**
 * A live crop-drag interaction, in SOURCE-pixel space. Three kinds:
 *  - `new`    — drawing a fresh rectangle from the down-point `(sx,sy)`.
 *  - `move`   — translating an existing `rect`; `(ox,oy)` = pointer offset
 *               INSIDE the rect at grab time (so the rect tracks the pointer).
 *  - `corner` — resizing `rect` by dragging `corner` (0 TL · 1 TR · 2 BR · 3 BL),
 *               with the OPPOSITE corner pinned.
 *
 * Held in a ref by the editor and read by the window-level mousemove/touchmove
 * listeners (see CropRotateEditor — the drag uses native mouse + touch events,
 * NOT the Pointer Events API, which dropped real-mouse drags in Safari AND
 * Chrome; the crop is resolved from window events against this state).
 */
export type CropDrag =
  | { kind: 'new'; sx: number; sy: number }
  | { kind: 'move'; ox: number; oy: number; rect: CropRect }
  | { kind: 'corner'; corner: 0 | 1 | 2 | 3; rect: CropRect };

/**
 * PURE — given a live drag, the current pointer position in source pixels, and
 * the source size, compute the new (unclamped-to-≥8) crop rect. The pointer is
 * clamped to the image bounds [0,W]×[0,H]; the rect is derived per drag kind.
 * Mirrors the previous inline onMove math exactly so behaviour is unchanged.
 */
export function resolveDrag(
  drag: CropDrag,
  px: number,
  py: number,
  W: number,
  H: number,
): CropRect {
  const cx = Math.max(0, Math.min(W, px));
  const cy = Math.max(0, Math.min(H, py));
  if (drag.kind === 'new') {
    const x = Math.min(drag.sx, cx);
    const y = Math.min(drag.sy, cy);
    return { x, y, w: Math.abs(cx - drag.sx), h: Math.abs(cy - drag.sy) };
  }
  if (drag.kind === 'move') {
    const x = Math.max(0, Math.min(W - drag.rect.w, cx - drag.ox));
    const y = Math.max(0, Math.min(H - drag.rect.h, cy - drag.oy));
    return { x, y, w: drag.rect.w, h: drag.rect.h };
  }
  // corner resize with the opposite corner pinned
  const r = drag.rect;
  const corners = [
    { x: r.x, y: r.y }, // 0 TL
    { x: r.x + r.w, y: r.y }, // 1 TR
    { x: r.x + r.w, y: r.y + r.h }, // 2 BR
    { x: r.x, y: r.y + r.h }, // 3 BL
  ];
  const opp = corners[(drag.corner + 2) % 4];
  const x = Math.min(opp.x, cx);
  const y = Math.min(opp.y, cy);
  return { x, y, w: Math.abs(opp.x - cx), h: Math.abs(opp.y - cy) };
}

/**
 * PURE — the natural pixel size of the BAKED image for a recipe over a W×H
 * source, under the "ROTATE THEN CROP" model. The user crops an UPRIGHT rect out
 * of the rotated/straightened image (whose size is the rotated bounding box):
 *   • no crop → the whole straightened image = the rotated bounding box (W'×H').
 *   • crop    → the crop rect's OWN w×h (the upright region selected), clamped
 *               to the rotated-bbox frame.
 * Used to set the derived FloorPlan width/height WITHOUT a canvas (so tests +
 * callers can predict dimensions). Returns whole pixels.
 */
export function bakedSize(
  recipe: EditRecipe,
  W: number,
  H: number,
): { width: number; height: number } {
  const deg = normaliseDeg(recipe.rotateDeg);
  const bbox = rotatedBBox(W, H, deg); // the straightened-image (display) frame
  const crop = clampCrop(recipe.crop, bbox.width, bbox.height);
  if (!crop) return { width: bbox.width, height: bbox.height };
  return { width: Math.max(1, Math.round(crop.w)), height: Math.max(1, Math.round(crop.h)) };
}

/**
 * PURE — derive a storage-safe DERIVED file name from the original. Keeps the
 * extension (forced to the baked MIME's extension), prefixes `derived-`, and is
 * deterministic for a given original name so a re-bake overwrites cleanly.
 *   "Ground Floor.png" → "derived-Ground Floor.jpg"  (when baking to jpeg)
 */
export function derivedFileName(originalName: string, mime: string): string {
  const ext = mime === 'image/png' ? 'png' : 'jpg';
  const base = (originalName || 'floorplan')
    .replace(/\.[a-z0-9]+$/i, '')
    .trim();
  const safeBase = base || 'floorplan';
  return `derived-${safeBase}.${ext}`;
}

/* ════════════════════ pure: FloorPlan ⇄ edited record ════════════════════ */

import type { FloorPlan } from './floorPlan';

/**
 * The ORIGINAL upload ref kept on an edited FloorPlan so the editor can re-open
 * against the source and the un-edited image is always recoverable. Bounded
 * subset of FloorPlan (no nested `original`/`edit` — flat, Firestore-safe).
 */
export interface OriginalImageRef {
  storagePath: string;
  downloadURL: string;
  contentType: string;
  width: number;
  height: number;
}

/** PURE — snapshot a FloorPlan as an OriginalImageRef (strips any edit fields). */
export function asOriginalRef(fp: FloorPlan): OriginalImageRef {
  return {
    storagePath: fp.storagePath,
    downloadURL: fp.downloadURL,
    contentType: fp.contentType,
    width: fp.width,
    height: fp.height,
  };
}

/**
 * An edited FloorPlan = a normal FloorPlan (pointing at the DERIVED image, so
 * the tracer + report use it transparently) PLUS the original ref + recipe.
 * `original`/`edit` are additive: a FloorPlan without them is an un-edited
 * upload and behaves exactly as before. Firestore-safe (flat maps, no classes).
 */
export interface EditedFloorPlan extends FloorPlan {
  original: OriginalImageRef;
  edit: EditRecipe;
}

/** PURE — does this FloorPlan carry an edit (is the visible image a derived one)? */
export function isEdited(
  fp: FloorPlan | null | undefined,
): fp is EditedFloorPlan {
  return (
    !!fp &&
    typeof (fp as EditedFloorPlan).original === 'object' &&
    (fp as EditedFloorPlan).original != null &&
    typeof (fp as EditedFloorPlan).edit === 'object'
  );
}

/**
 * PURE — the ORIGINAL image ref to open the editor against. For an edited plan
 * that is `fp.original`; for a plain upload it is the plan itself (snapshotted).
 * The editor always edits FROM the pristine source, never the baked output, so
 * repeated edits never compound rotation/crop loss.
 */
export function sourceRefFor(fp: FloorPlan): OriginalImageRef {
  return isEdited(fp) ? fp.original : asOriginalRef(fp);
}

/** PURE — the recipe currently applied (identity for a plain upload). */
export function recipeFor(fp: FloorPlan): EditRecipe {
  return isEdited(fp) ? fp.edit : { ...IDENTITY_RECIPE };
}

/**
 * PURE — hydrate the original ref / recipe from a stored FloorPlan doc map
 * (defensive — a doc may pre-date the edit feature). Returns null when absent.
 */
export function editFromDoc(d: Record<string, unknown> | null | undefined): {
  original: OriginalImageRef;
  edit: EditRecipe;
} | null {
  if (!d || typeof d !== 'object') return null;
  const o = d.original as Record<string, unknown> | undefined;
  const e = d.edit as Record<string, unknown> | undefined;
  if (!o || typeof o !== 'object') return null;
  const url = typeof o.downloadURL === 'string' ? o.downloadURL : '';
  const sp = typeof o.storagePath === 'string' ? o.storagePath : '';
  if (!url && !sp) return null;
  const original: OriginalImageRef = {
    storagePath: sp,
    downloadURL: url,
    contentType: typeof o.contentType === 'string' ? o.contentType : '',
    width: typeof o.width === 'number' ? o.width : 0,
    height: typeof o.height === 'number' ? o.height : 0,
  };
  let crop: CropRect | null = null;
  const c = e && (e.crop as Record<string, unknown> | null | undefined);
  if (c && typeof c === 'object') {
    crop = {
      x: typeof c.x === 'number' ? c.x : 0,
      y: typeof c.y === 'number' ? c.y : 0,
      w: typeof c.w === 'number' ? c.w : 0,
      h: typeof c.h === 'number' ? c.h : 0,
    };
  }
  const rotateDeg =
    e && typeof e.rotateDeg === 'number' ? normaliseDeg(e.rotateDeg) : 0;
  return { original, edit: { crop, rotateDeg } };
}

/** PURE — the additive doc body for the edit fields (merge onto a FloorPlan). */
export function editDocBody(
  original: OriginalImageRef,
  edit: EditRecipe,
): Record<string, unknown> {
  return {
    original: {
      storagePath: original.storagePath,
      downloadURL: original.downloadURL,
      contentType: original.contentType,
      width: original.width,
      height: original.height,
    },
    edit: {
      crop: edit.crop
        ? { x: edit.crop.x, y: edit.crop.y, w: edit.crop.w, h: edit.crop.h }
        : null,
      rotateDeg: normaliseDeg(edit.rotateDeg),
    },
  };
}

/* ════════════════════ pure: source-type detection ════════════════════ */

/**
 * PURE — is the source a PDF (not a raster)? A PDF cannot be loaded by
 * `new Image()` and cannot be drawn to a canvas directly — it must be
 * rasterised first (see `loadEditableSource`). Matches on the stored
 * contentType OR a `.pdf` extension on the storage path / URL (Adobe Scan
 * uploads arrive as `application/pdf`).
 */
export function isPdfSource(
  contentType: string | null | undefined,
  ref?: string | null,
): boolean {
  const ct = (contentType || '').toLowerCase();
  if (ct === 'application/pdf') return true;
  const r = (ref || '').toLowerCase();
  // strip a query string (Firebase download URLs carry ?alt=media&token=…)
  const path = r.split('?')[0];
  return path.endsWith('.pdf');
}

/* ════════════════════ impure: bake the canvas + load bitmaps ════════════════════ */

/** The MIME a derived image bakes to. JPEG keeps plan rasters small; if the
 *  source has alpha (PNG) we keep PNG so transparency isn't blacked out. A PDF
 *  source rasterises to JPEG (no alpha) — handled by `loadEditableSource`. */
export function bakeMimeFor(sourceMime: string): string {
  return (sourceMime || '').toLowerCase() === 'image/png'
    ? 'image/png'
    : 'image/jpeg';
}

/**
 * The resolved, READY-TO-BAKE source. `image` is a CanvasImageSource that
 * `bakeImage` can draw (an <img> for a raster, a <canvas> for a rasterised PDF
 * page). `previewUrl` is a SAME-ORIGIN object/data URL the editor can show in an
 * <img> (so the preview works for PDFs too and never canvas-taints). Call
 * `revoke()` when the editor closes to free any object URL.
 */
export interface EditableSource {
  image: CanvasImageSource;
  width: number;
  height: number;
  previewUrl: string;
  /** the mime to bake to (PDF → image/jpeg; otherwise from the source). */
  bakeMime: string;
  revoke: () => void;
}

/** PDF rasterise scale — 2× so a scanned plan stays crisp enough to trace. */
const PDF_RASTER_SCALE = 2;

/**
 * Load pdfjs-dist (with its worker wired) ONCE and open a PDF document from
 * bytes. `getDocument` consumes its `data` buffer (transfers/neuters it), so the
 * caller must pass a buffer it doesn't need afterwards — every public helper here
 * reads its own `arrayBuffer()` so a File can be opened more than once.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function openPdfDoc(bytes: ArrayBuffer): Promise<any> {
  const pdfjs = await import('pdfjs-dist');
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  (
    pdfjs as unknown as { GlobalWorkerOptions: { workerSrc: string } }
  ).GlobalWorkerOptions.workerSrc = (worker as { default: string }).default;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (pdfjs as any).getDocument({ data: bytes }).promise;
}

/**
 * The number of pages in a PDF (given as bytes). Used by the upload flow to
 * decide whether to show the page picker: a single-page PDF rasterises page 1
 * silently; a multi-page PDF lets the staff CHOOSE the page that holds the floor
 * plan (the owner's scans often have a report cover on page 1). Throws a clear
 * message on a non-PDF / corrupt file.
 */
export async function getPdfPageCount(bytes: ArrayBuffer): Promise<number> {
  const doc = await openPdfDoc(bytes);
  const n = Number(doc.numPages);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/**
 * Rasterise ONE page (1-based) of a PDF (given as bytes) to a canvas via
 * pdfjs-dist (already a dependency; same import style as ReportPdfViewer).
 * `scale` defaults to the 2× trace-quality raster; the page picker passes a small
 * scale for thumbnails. The page index is clamped to [1, numPages]. Returns the
 * canvas — the caller draws/exports from it. Throws a clear message on failure.
 */
export async function rasterisePdfPage(
  bytes: ArrayBuffer,
  pageNum = 1,
  scale: number = PDF_RASTER_SCALE,
): Promise<HTMLCanvasElement> {
  const doc = await openPdfDoc(bytes);
  const total = Number(doc.numPages) || 1;
  const idx = Math.max(1, Math.min(total, Math.round(pageNum) || 1));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const page: any = await doc.getPage(idx);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(viewport.width));
  canvas.height = Math.max(1, Math.round(viewport.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported in this browser.');
  // white paper under the page (PDFs are transparent → black corners otherwise).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas;
}

/**
 * Rasterise the FIRST page of a PDF (back-compat shim — the editor's
 * `loadEditableSource` path always rasterises page 1, since by upload time the
 * chosen page has already been baked into the stored image). Delegates to
 * `rasterisePdfPage(bytes, 1)`.
 */
export async function rasterisePdfFirstPage(
  bytes: ArrayBuffer,
): Promise<HTMLCanvasElement> {
  return rasterisePdfPage(bytes, 1);
}

/** Thumbnail render scale cap — the LONG edge of the rendered thumb in CSS px.
 *  Small so a many-page PDF renders quickly + cheaply in the page picker. */
const PDF_THUMB_MAX_PX = 220;

/**
 * Render ONE page of a PDF to a small JPEG data URL for the page-picker grid.
 * The render scale is chosen so the page's LONG edge is ≤ `maxPx` (default
 * `PDF_THUMB_MAX_PX`) — cheap to render even for a big plan. Returns a
 * self-contained data URL (no object URL to revoke). Throws on failure.
 */
export async function renderPdfThumb(
  bytes: ArrayBuffer,
  pageNum: number,
  maxPx: number = PDF_THUMB_MAX_PX,
): Promise<string> {
  const doc = await openPdfDoc(bytes);
  const total = Number(doc.numPages) || 1;
  const idx = Math.max(1, Math.min(total, Math.round(pageNum) || 1));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const page: any = await doc.getPage(idx);
  const base = page.getViewport({ scale: 1 });
  const longEdge = Math.max(base.width, base.height) || 1;
  const scale = Math.min(1.5, Math.max(0.05, maxPx / longEdge));
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(viewport.width));
  canvas.height = Math.max(1, Math.round(viewport.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported in this browser.');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL('image/jpeg', 0.7);
}

/** Promisified canvas→Blob. */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) =>
        b ? resolve(b) : reject(new Error('Could not rasterise the page.')),
      mime,
      quality,
    ),
  );
}

/**
 * Convert a chosen PDF File into a rasterised IMAGE File (page `pageNum`, 1-based,
 * → JPEG). Used at UPLOAD time so the floor-plan stored in `/floorplans/` is
 * always an image (the Storage rule is image-only) AND so the crop/rotate +
 * tracing + report pipeline downstream is identical to a normal image upload.
 * `pageNum` defaults to 1 (a single-page PDF, or callers that don't pick);
 * the multi-page upload flow passes the page the staff chose in the page picker.
 * The derived image name reuses the PDF base name with a `.jpg` extension (a
 * `-pN` suffix when a page other than 1 was picked, so a re-upload of a different
 * page lands a distinct object). Returns the File + its natural pixel size (the
 * tracer's coordinate space). Throws a clear message.
 */
export async function pdfFileToImageFile(
  file: File,
  pageNum = 1,
): Promise<{ file: File; width: number; height: number }> {
  const canvas = await rasterisePdfPage(await file.arrayBuffer(), pageNum);
  const blob = await canvasToBlob(canvas, 'image/jpeg', 0.92);
  const base = (file.name || 'floorplan').replace(/\.[a-z0-9]+$/i, '').trim();
  const page = Math.max(1, Math.round(pageNum) || 1);
  const suffix = page > 1 ? `-p${page}` : '';
  const name = `${base || 'floorplan'}${suffix}.jpg`;
  return {
    file: new File([blob], name, { type: 'image/jpeg' }),
    width: canvas.width,
    height: canvas.height,
  };
}

/** Load an <img> from an (already same-origin) object/data URL. */
function loadImgFromSameOriginUrl(
  url: string,
): Promise<{ img: HTMLImageElement; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ img, width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Could not load that image to edit.'));
    img.src = url;
  });
}

/**
 * Fetch the raw bytes of a Storage source as a Blob — the ROOT-CAUSE fix for
 * "Could not load that file to edit".
 *
 * ── WHY THE STORAGE SDK, NOT `fetch(downloadURL)` (read before changing): ──
 * The earlier attempt did `fetch(downloadURL, {mode:'cors'})` then, on failure,
 * fell back to `<img crossOrigin='anonymous'>`. BOTH paths hit the bucket's
 * `firebasestorage.app` download host as a cross-origin browser request — and
 * that bucket sends NO `Access-Control-Allow-Origin` header for our web origin,
 * so the browser BLOCKS the fetch AND taints/blocks the <img>. Result: every
 * source (even a plain 800×600 PNG) failed to load.
 *
 * The robust fix is to read the object through the **Firebase Storage SDK**
 * (`getBlob(ref(storage, storagePath))`). The SDK issues an AUTHENTICATED
 * request the Storage service answers with the correct CORS headers for the
 * SDK flow — it is NOT the raw cross-origin download-URL fetch, so it is not
 * subject to the missing-CORS-on-the-bucket problem. The returned Blob → an
 * object URL is same-origin, so the bake canvas is NEVER tainted and
 * `toBlob()` / `getImageData()` always succeed. No bucket CORS config needed.
 *
 * We prefer the storagePath (SDK) and only fall back to a plain URL fetch when
 * no path is known (e.g. a legacy record that stored only a downloadURL).
 */
async function fetchSourceBlob(
  storagePath: string | null | undefined,
  url: string,
): Promise<Blob> {
  const path = (storagePath || '').trim();
  if (path) {
    // Primary, robust path: authenticated SDK read → no CORS canvas taint.
    const { getBlob, ref } = await import('firebase/storage');
    const { storage } = await import('./firebase');
    return getBlob(ref(storage, path));
  }
  // Fallback only when we have no storage path: a plain URL fetch (works if the
  // bucket happens to be CORS-configured; otherwise it throws and the caller
  // surfaces a clear error).
  if (!url) throw new Error('No image source to load.');
  const resp = await fetch(url, { mode: 'cors' });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.blob();
}

/**
 * Resolve ANY floor-plan / screenshot source into a ready-to-bake +
 * previewable raster — the ONE entry point the editor + bake path use.
 *
 * Handles the two failure modes the owner hit:
 *   • PDF source ("Adobe Scan …pdf") — `new Image()` can't load a PDF, so we
 *     read the bytes and rasterise page 1 to a canvas (the bake source) +
 *     export it to an object URL (the editor preview). No <img> ever sees a PDF.
 *   • CORS canvas-taint — instead of `fetch(downloadURL)` /
 *     `new Image(); crossOrigin='anonymous'` (which FAIL when the bucket echoes
 *     no CORS for this origin — the actual bug), we read the object through the
 *     Storage SDK (`getBlob`) and load it via a same-origin object URL. The
 *     canvas is NEVER tainted and `toBlob` always succeeds.
 *
 * Pass the `storagePath` (preferred — drives the SDK read). `url` is the
 * download URL, used only as a legacy fallback when no storagePath is known.
 *
 * Always call `revoke()` when done to free the object URL.
 */
export async function loadEditableSource(
  url: string,
  contentType: string | null | undefined,
  storagePath?: string | null,
): Promise<EditableSource> {
  if (!url && !storagePath) throw new Error('No image source to load.');

  // Read the bytes once via the Storage SDK (storagePath) — no CORS taint —
  // and rasterise/object-URL from that blob.
  let blob: Blob;
  try {
    blob = await fetchSourceBlob(storagePath, url);
  } catch (e) {
    // SDK/URL read failed. For a PDF there is no <img> fallback. For a raster,
    // try the legacy CORS <img> loader so a CORS-configured bucket still works.
    if (isPdfSource(contentType, storagePath || url)) {
      throw new Error(
        `Could not read that PDF to edit (${(e as Error).message}).`,
      );
    }
    if (!url) throw e;
    const { img, width, height } = await loadImageEl(url);
    return {
      image: img,
      width,
      height,
      previewUrl: url,
      bakeMime: bakeMimeFor(contentType || ''),
      revoke: () => {},
    };
  }

  const effectiveType = blob.type || contentType || '';
  if (isPdfSource(effectiveType, storagePath || url)) {
    const canvas = await rasterisePdfFirstPage(await blob.arrayBuffer());
    const raster = await canvasToBlob(canvas, 'image/jpeg', 0.92);
    const previewUrl = URL.createObjectURL(raster);
    return {
      image: canvas, // draw straight from the rasterised canvas when baking
      width: canvas.width,
      height: canvas.height,
      previewUrl,
      bakeMime: 'image/jpeg',
      revoke: () => URL.revokeObjectURL(previewUrl),
    };
  }

  // Raster image — object URL is same-origin ⇒ canvas never taints.
  const objUrl = URL.createObjectURL(blob);
  try {
    const { img, width, height } = await loadImgFromSameOriginUrl(objUrl);
    return {
      image: img,
      width,
      height,
      previewUrl: objUrl,
      bakeMime: bakeMimeFor(effectiveType),
      revoke: () => URL.revokeObjectURL(objUrl),
    };
  } catch (e) {
    URL.revokeObjectURL(objUrl);
    throw e;
  }
}

/**
 * Load an image element from a URL (CORS-enabled so the canvas is NOT tainted —
 * Firebase Storage download URLs serve permissive CORS). Resolves the element +
 * its natural size. Used to bake from a Storage source.
 */
export function loadImageEl(
  url: string,
): Promise<{ img: HTMLImageElement; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No image URL to load.'));
      return;
    }
    const img = new Image();
    // crossOrigin so drawImage→toBlob does not taint the canvas (export works).
    img.crossOrigin = 'anonymous';
    img.onload = () =>
      resolve({ img, width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Could not load that image to edit.'));
    img.src = url;
  });
}

/**
 * Load an image element from a local File (object URL). No CORS concern — same
 * origin. Used to bake straight from a just-uploaded File without a round-trip.
 */
export function loadImageFromFile(
  file: File,
): Promise<{ img: HTMLImageElement; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const out = { img, width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(out);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image to edit.'));
    };
    img.src = url;
  });
}

/**
 * BAKE — render `img` (natural srcW×srcH) through `recipe` under the
 * **ROTATE → CROP** model and return a File of the baked pixels + its natural
 * size.
 *
 * Step 1 — ROTATE (straighten): draw the source rotated by `deg` onto an
 *   intermediate canvas sized to the rotated bounding box (W'×H'). White paper
 *   under JPEG so the rotation's triangular corners read as paper, not black.
 * Step 2 — CROP (upright): the user's crop rect is an UPRIGHT rectangle in that
 *   rotated-bbox frame; copy that axis-aligned region to the output canvas
 *   (sized to the crop's own w×h). No crop → the whole straightened image.
 *
 * Output = the straightened, upright, cropped region the owner drew. Throws if
 * the canvas 2D context or `toBlob` is unavailable.
 */
export async function bakeImage(
  img: CanvasImageSource,
  srcW: number,
  srcH: number,
  recipe: EditRecipe,
  derivedName: string,
  sourceMime: string,
): Promise<{ file: File; width: number; height: number }> {
  const mime = bakeMimeFor(sourceMime);
  const deg = normaliseDeg(recipe.rotateDeg);
  const rad = (deg * Math.PI) / 180;
  const bbox = rotatedBBox(srcW, srcH, deg); // straightened-image (display) frame

  // ── Step 1: ROTATE the source onto the rotated-bbox canvas (the straightened
  // image, axis-aligned to the screen). ──
  const rotated = document.createElement('canvas');
  rotated.width = bbox.width;
  rotated.height = bbox.height;
  const rctx = rotated.getContext('2d');
  if (!rctx) throw new Error('Canvas not supported in this browser.');
  if (mime === 'image/jpeg') {
    rctx.fillStyle = '#ffffff';
    rctx.fillRect(0, 0, bbox.width, bbox.height);
  }
  rctx.save();
  rctx.translate(bbox.width / 2, bbox.height / 2);
  rctx.rotate(rad);
  rctx.imageSmoothingQuality = 'high';
  // source drawn centred on the rotated origin → fills the rotated bbox.
  rctx.drawImage(img, -srcW / 2, -srcH / 2, srcW, srcH);
  rctx.restore();

  // ── Step 2: CROP the user's UPRIGHT rect out of the straightened image. ──
  const crop = clampCrop(recipe.crop, bbox.width, bbox.height);
  const cx = crop ? crop.x : 0;
  const cy = crop ? crop.y : 0;
  const width = crop ? Math.max(1, Math.round(crop.w)) : bbox.width;
  const height = crop ? Math.max(1, Math.round(crop.h)) : bbox.height;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported in this browser.');
  if (mime === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }
  ctx.imageSmoothingQuality = 'high';
  // axis-aligned copy of the crop region from the straightened image. No
  // rotation here — the rect is already upright in the rotated-bbox frame.
  ctx.drawImage(rotated, cx, cy, width, height, 0, 0, width, height);

  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(
      (b) => resolve(b),
      mime,
      mime === 'image/jpeg' ? 0.92 : undefined,
    ),
  );
  if (!blob) throw new Error('Could not export the edited image.');
  const file = new File([blob], derivedName, { type: mime });
  return { file, width, height };
}
